/**
 * Master data hooks — suppliers, clients, departments.
 *
 * All three follow the same shape (name + notes + isActive + soft-delete
 * fields), so the hooks are parameterised. We expose a typed surface per
 * entity to keep IntelliSense useful in the UI.
 *
 * Reference lists are managed in Settings, while suppliers and clients have dedicated modules. Deactivation is a soft
 * delete; the API rejects hard deletes for masters that are referenced.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';

interface PaginatedList<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------

export interface SupplierRecord {
  id: string;
  name: string;
  address: string | null;
  categoryId: string | null;
  subcategoryId: string | null;
  contactPerson: string | null;
  officialPhone: string | null;
  personalPhone: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export interface SupplierCreateInput {
  name: string;
  address?: string;
  categoryId?: string | null;
  subcategoryId?: string | null;
  contactPerson?: string;
  officialPhone?: string;
  personalPhone?: string;
  notes?: string;
}

export interface SupplierUpdateInput {
  name?: string;
  address?: string | null;
  categoryId?: string | null;
  subcategoryId?: string | null;
  contactPerson?: string | null;
  officialPhone?: string | null;
  personalPhone?: string | null;
  notes?: string | null;
  isActive?: boolean;
  rowVersion: number;
}

export const suppliersListAllKey = ['masters', 'suppliers'] as const;

export function useSuppliersListAll() {
  return useQuery({
    queryKey: suppliersListAllKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<SupplierRecord>>('/api/suppliers?pageSize=200', {
        signal,
      });
      return res.items;
    },
    staleTime: 60_000,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation<SupplierRecord, ApiError, SupplierCreateInput>({
    mutationFn: (body) => apiRequest<SupplierRecord>('/api/suppliers', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: suppliersListAllKey });
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation<SupplierRecord, ApiError, { id: string } & SupplierUpdateInput>({
    mutationFn: ({ id, ...body }) =>
      apiRequest<SupplierRecord>(`/api/suppliers/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: suppliersListAllKey });
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation<null, ApiError, { id: string; rowVersion: number }>({
    mutationFn: ({ id, rowVersion }) =>
      apiRequest<null>(`/api/suppliers/${id}`, {
        method: 'DELETE',
        query: { rowVersion },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: suppliersListAllKey });
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}


// ---------------------------------------------------------------------------
// Supplier categories / sub-categories
// ---------------------------------------------------------------------------

export interface SupplierCategoryRecord {
  id: string;
  parentCategoryId: string | null;
  name: string;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export interface SupplierCategoryCreateInput {
  parentCategoryId?: string | null;
  name: string;
  notes?: string;
}

export interface SupplierCategoryUpdateInput {
  parentCategoryId?: string | null;
  name?: string;
  notes?: string | null;
  isActive?: boolean;
  rowVersion: number;
}

export const supplierCategoriesListAllKey = ['masters', 'supplier-categories'] as const;

export function useSupplierCategoriesListAll() {
  return useQuery({
    queryKey: supplierCategoriesListAllKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<SupplierCategoryRecord>>(
        '/api/supplier-categories?pageSize=200',
        { signal },
      );
      return res.items;
    },
    staleTime: 60_000,
  });
}

export function useCreateSupplierCategory() {
  const qc = useQueryClient();
  return useMutation<SupplierCategoryRecord, ApiError, SupplierCategoryCreateInput>({
    mutationFn: (body) =>
      apiRequest<SupplierCategoryRecord>('/api/supplier-categories', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supplierCategoriesListAllKey });
    },
  });
}

export function useUpdateSupplierCategory() {
  const qc = useQueryClient();
  return useMutation<SupplierCategoryRecord, ApiError, { id: string } & SupplierCategoryUpdateInput>({
    mutationFn: ({ id, ...body }) =>
      apiRequest<SupplierCategoryRecord>(`/api/supplier-categories/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supplierCategoriesListAllKey });
      void qc.invalidateQueries({ queryKey: suppliersListAllKey });
    },
  });
}

export function useDeleteSupplierCategory() {
  const qc = useQueryClient();
  return useMutation<null, ApiError, { id: string; rowVersion: number }>({
    mutationFn: ({ id, rowVersion }) =>
      apiRequest<null>(`/api/supplier-categories/${id}`, {
        method: 'DELETE',
        query: { rowVersion },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supplierCategoriesListAllKey });
      void qc.invalidateQueries({ queryKey: suppliersListAllKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export interface ClientRecord {
  id: string;
  name: string;
  address: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export interface ClientCreateInput {
  name: string;
  address?: string;
  notes?: string;
}

export interface ClientUpdateInput {
  name?: string;
  address?: string | null;
  notes?: string | null;
  isActive?: boolean;
  rowVersion: number;
}

export const clientsListAllKey = ['masters', 'clients'] as const;

export function useClientsListAll() {
  return useQuery({
    queryKey: clientsListAllKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<ClientRecord>>('/api/clients?pageSize=200', {
        signal,
      });
      return res.items;
    },
    staleTime: 60_000,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation<ClientRecord, ApiError, ClientCreateInput>({
    mutationFn: (body) => apiRequest<ClientRecord>('/api/clients', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsListAllKey });
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation<ClientRecord, ApiError, { id: string } & ClientUpdateInput>({
    mutationFn: ({ id, ...body }) =>
      apiRequest<ClientRecord>(`/api/clients/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsListAllKey });
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation<null, ApiError, { id: string; rowVersion: number }>({
    mutationFn: ({ id, rowVersion }) =>
      apiRequest<null>(`/api/clients/${id}`, {
        method: 'DELETE',
        query: { rowVersion },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsListAllKey });
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

export interface DepartmentRecord {
  id: string;
  clientId: string | null;
  parentDepartmentId: string | null;
  name: string;
  contactName: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  fax: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export interface DepartmentCreateInput {
  clientId: string;
  parentDepartmentId?: string | null;
  name: string;
  contactName?: string;
  address?: string;
  phone?: string;
  email?: string;
  fax?: string;
  notes?: string;
}

export interface DepartmentUpdateInput {
  clientId?: string;
  parentDepartmentId?: string | null;
  name?: string;
  contactName?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  fax?: string | null;
  notes?: string | null;
  isActive?: boolean;
  rowVersion: number;
}

export const departmentsListAllKey = ['masters', 'departments'] as const;

export function useDepartmentsListAll() {
  return useQuery({
    queryKey: departmentsListAllKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<DepartmentRecord>>(
        '/api/departments?pageSize=200',
        { signal },
      );
      return res.items;
    },
    staleTime: 60_000,
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation<DepartmentRecord, ApiError, DepartmentCreateInput>({
    mutationFn: (body) =>
      apiRequest<DepartmentRecord>('/api/departments', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: departmentsListAllKey });
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation<DepartmentRecord, ApiError, { id: string } & DepartmentUpdateInput>({
    mutationFn: ({ id, ...body }) =>
      apiRequest<DepartmentRecord>(`/api/departments/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: departmentsListAllKey });
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation<null, ApiError, { id: string; rowVersion: number }>({
    mutationFn: ({ id, rowVersion }) =>
      apiRequest<null>(`/api/departments/${id}`, {
        method: 'DELETE',
        query: { rowVersion },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: departmentsListAllKey });
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Incoterms / Terms of delivery
// ---------------------------------------------------------------------------

export interface IncotermRecord {
  id: string;
  code: string;
  label: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export interface IncotermCreateInput {
  code: string;
  label?: string;
  notes?: string;
}

export interface IncotermUpdateInput {
  code?: string;
  label?: string | null;
  notes?: string | null;
  isActive?: boolean;
  rowVersion: number;
}

export const incotermsListAllKey = ['masters', 'incoterms'] as const;

export function useIncotermsListAll() {
  return useQuery({
    queryKey: incotermsListAllKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<IncotermRecord>>('/api/incoterms?pageSize=200', {
        signal,
      });
      return res.items;
    },
    staleTime: 60_000,
  });
}

export function useCreateIncoterm() {
  const qc = useQueryClient();
  return useMutation<IncotermRecord, ApiError, IncotermCreateInput>({
    mutationFn: (body) => apiRequest<IncotermRecord>('/api/incoterms', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: incotermsListAllKey });
    },
  });
}

export function useUpdateIncoterm() {
  const qc = useQueryClient();
  return useMutation<IncotermRecord, ApiError, { id: string } & IncotermUpdateInput>({
    mutationFn: ({ id, ...body }) =>
      apiRequest<IncotermRecord>(`/api/incoterms/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: incotermsListAllKey });
    },
  });
}

export function useDeleteIncoterm() {
  const qc = useQueryClient();
  return useMutation<null, ApiError, { id: string; rowVersion: number }>({
    mutationFn: ({ id, rowVersion }) =>
      apiRequest<null>(`/api/incoterms/${id}`, {
        method: 'DELETE',
        query: { rowVersion },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: incotermsListAllKey });
    },
  });
}
